import { AxiosResponse } from 'axios';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { SyncedBy } from '~/@types/transactions';
import { QueryKeys } from '~/api/queryKeys';
import { OpportunityJSONAPI } from '~/api/resources/opportunity';
import { UpdatedRecordsMeta } from '~/components/agents/types';
import queryClient from '~/contexts/queryClient';
import { SelectedRowsProps } from '~/hooks/table/useSelectedRows';
import useLocales from '~/hooks/useLocales';

type Args = {
  clearSelectedRows: Pick<SelectedRowsProps, 'clear'>['clear'];
};

export const useCallbacks = ({ clearSelectedRows }: Args) => {
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const onSyncSuccess = (syncedBy: SyncedBy[]) => {
    const opportunitiesData: AxiosResponse<any, any> | undefined = queryClient.getQueryData(
      QueryKeys.opportunities()
    );

    if (!opportunitiesData) return;

    syncedBy.forEach((syncedByData) => {
      const index = opportunitiesData.data.data.findIndex(
        (opportunity: OpportunityJSONAPI) => opportunity.id === syncedByData.loan_transaction_id
      );

      if (index < 0) return;

      delete syncedByData.list_id;

      const syncedByDataUpdated = opportunitiesData.data.data[index].attributes.synced_by || [];

      syncedByDataUpdated.unshift(syncedByData);
      opportunitiesData.data.data[index].attributes.synced_by = syncedByDataUpdated;
      opportunitiesData.data.data[index].attributes.synced_with_bpd_crm = true;

      const addedData = {
        list_id: syncedByData.list_id,
        loan_transaction_id: syncedByData.loan_transaction_id,
        name: t('dashboard.lists.default_list'),
      };
      const addedDataUpdated = opportunitiesData.data.data[index].attributes.added || { lists: [] };
      addedDataUpdated.lists.unshift(addedData);
      opportunitiesData.data.data[index].attributes.added = addedDataUpdated;
    });

    queryClient.setQueryData(QueryKeys.opportunities(), opportunitiesData);
    clearSelectedRows();
  };

  const onAddToListSuccess = (updatedRecords: UpdatedRecordsMeta, selectedRecordsIds: string[]) => {
    const opportunitiesData: AxiosResponse<any, any> | undefined = queryClient.getQueryData(
      QueryKeys.opportunities()
    );

    if (!opportunitiesData || updatedRecords === null) {
      enqueueSnackbar('Opportunities are already in the list', { variant: 'info' });
      return;
    }

    if (Object.keys(updatedRecords.added).length < selectedRecordsIds.length) {
      enqueueSnackbar('Some opportunities are already in the list', { variant: 'info' });
    }

    Object.keys(updatedRecords.added).forEach((id: string) => {
      const opportunitiesDataIndex = opportunitiesData.data.data.findIndex(
        (opportunity: OpportunityJSONAPI) => opportunity.id === id
      );
      opportunitiesData.data.data[opportunitiesDataIndex].attributes.added =
        updatedRecords.added[Number(id)];
      opportunitiesData.data.data[opportunitiesDataIndex].attributes.synced_by =
        updatedRecords.synced[Number(id)];
      if (!isEmpty(updatedRecords.synced[Number(id)])) {
        opportunitiesData.data.data[opportunitiesDataIndex].attributes.synced_with_bpd_crm = true;
      }
    });

    queryClient.setQueryData(QueryKeys.opportunities(), opportunitiesData);
    clearSelectedRows();
  };

  return {
    onSyncSuccess,
    onAddToListSuccess,
  };
};
